import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };
const money = (v) => `${Number(v || 0) < 0 ? '−' : ''}$${Math.abs(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (v) => (Math.abs(Number(v || 0)) < 0.005 ? '—' : `${Number(v) > 0 ? '+' : '−'}${money(Math.abs(v))}`);
const deltaCls = (v, invert = false) => {
  if (Math.abs(Number(v || 0)) < 0.005) return 'text-slate-400';
  const up = Number(v) > 0;
  return (invert ? !up : up) ? 'text-emerald-700' : 'text-red-700';
};

export const DIFF_COLUMNS = [
  { key: 'gross', label: 'Gross' },
  { key: 'admin_fee', label: 'Admin fee', deduction: true },
  { key: 'lease_on', label: 'Lease-on', deduction: true },
  { key: 'escrow', label: 'Escrow', deduction: true },
  { key: 'advances', label: 'Advances', deduction: true },
  { key: 'other_deductions', label: 'Other ded.', deduction: true },
  { key: 'net', label: 'Net pay' },
];

export const useRevisionDiff = (payrollId, enabled = true) => {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!enabled || !payrollId) { setDiff(null); return; }
    let active = true;
    setLoading(true);
    axios.get(`${API}/payrolls/${payrollId}/revision-diff`, { withCredentials: true })
      .then((r) => { if (active) setDiff(r.data); })
      .catch(() => { if (active) setDiff(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [payrollId, enabled]);
  return { diff, loading };
};

/** Compact totals delta used inside the Send confirmation. */
export const RevisionDiffSummary = ({ diff }) => {
  if (!diff) return null;
  const s = diff.summary;
  return (
    <div data-testid="revision-diff-summary" className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900" style={FONT}>
      <p className="font-semibold mb-1">Changes vs the sent version: {s.changed} changed · {s.added} added · {s.removed} removed · {s.unchanged} unchanged</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {DIFF_COLUMNS.map((c) => (
          <div key={c.key} className="flex justify-between gap-2">
            <span>{c.label}</span>
            <span data-testid={`diff-summary-${c.key}`} className={`font-semibold ${deltaCls(diff.totals[c.key].delta, c.deduction)}`}>{money(diff.totals[c.key].sent)} <ArrowRight className="inline h-3 w-3" /> {money(diff.totals[c.key].revised)} ({signed(diff.totals[c.key].delta)})</span>
          </div>
        ))}
      </div>
      {(diff.adjustments.sent.compensation !== diff.adjustments.revised.compensation || diff.adjustments.sent.custom_deductions !== diff.adjustments.revised.custom_deductions) && (
        <p className="mt-1">Run adjustments: compensation {money(diff.adjustments.sent.compensation)} → {money(diff.adjustments.revised.compensation)} · custom deductions {money(diff.adjustments.sent.custom_deductions)} → {money(diff.adjustments.revised.custom_deductions)}</p>
      )}
    </div>
  );
};

/** Full side-by-side table shown on the pending revision page. */
export const RevisionDiffPanel = ({ payrollId }) => {
  const { diff, loading } = useRevisionDiff(payrollId);
  const [showUnchanged, setShowUnchanged] = useState(false);
  if (loading) return <div className="mb-6 flex items-center gap-2 text-sm text-slate-500" style={FONT}><Loader2 className="h-4 w-4 animate-spin" /> Comparing with the sent version…</div>;
  if (!diff) return null;
  const rows = diff.rows.filter((r) => showUnchanged || r.status !== 'unchanged');
  return (
    <div data-testid="revision-diff-panel" className="mb-6 rounded-md border border-blue-200 bg-white overflow-hidden" style={FONT}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-100 bg-blue-50 px-4 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-800">Sent vs revised — per driver</p>
          <p className="text-xs text-blue-900 mt-0.5">{diff.summary.changed} changed · {diff.summary.added} added · {diff.summary.removed} removed · {diff.summary.unchanged} unchanged · net change <span data-testid="revision-diff-net-delta" className={`font-bold ${deltaCls(diff.summary.net_delta)}`}>{signed(diff.summary.net_delta)}</span></p>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" data-testid="revision-diff-show-unchanged" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} /> show unchanged</label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Driver</th>
              {DIFF_COLUMNS.map((c) => <th key={c.key} className="text-right px-3 py-2">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.driver_id} data-testid={`revision-diff-row-${r.driver_id}`} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2">
                  <p className="font-semibold text-slate-800">{r.driver_name || r.driver_id}</p>
                  <span className={`inline-block mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${r.status === 'added' ? 'bg-emerald-100 text-emerald-800' : r.status === 'removed' ? 'bg-red-100 text-red-800' : r.status === 'changed' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{r.status}</span>
                </td>
                {DIFF_COLUMNS.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="text-slate-500">{r.sent.present ? money(r.sent[c.key]) : '—'}</div>
                    <div className="font-semibold text-slate-900">{r.revised.present ? money(r.revised[c.key]) : '—'}</div>
                    <div className={`text-[11px] font-semibold ${deltaCls(r.delta[c.key], c.deduction)}`}>{signed(r.delta[c.key])}</div>
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={DIFF_COLUMNS.length + 1} className="px-3 py-4 text-center text-slate-500">No per-driver changes — amounts are identical to the sent version.</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <td className="px-3 py-2 text-slate-700">Totals (sent / revised / change)</td>
              {DIFF_COLUMNS.map((c) => (
                <td key={c.key} className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="text-slate-500 font-normal">{money(diff.totals[c.key].sent)}</div>
                  <div className="text-slate-900">{money(diff.totals[c.key].revised)}</div>
                  <div className={`text-[11px] ${deltaCls(diff.totals[c.key].delta, c.deduction)}`}>{signed(diff.totals[c.key].delta)}</div>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100">Grey = sent version · bold = this revision · deductions shown as amounts withheld (a lower deduction reads as a gain for the driver).</p>
    </div>
  );
};
